// AsteroidBelt.tsx — A ring of instanced low-poly rocks between two radii, each on
// its own near-circular orbit at a Kepler angular speed (inner rocks faster),
// advanced per frame. Deterministic scatter from the belt seed; one InstancedMesh.
import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Euler, Matrix4, Quaternion, Vector3 } from 'three';
import type { InstancedMesh } from 'three';
import type { AsteroidBelt as BeltData } from '../types';
import { makeRng } from '../galaxy/rng';
import { gameClock } from '../state/gameClock';

interface Props {
  belt: BeltData;
}

const TWO_PI = Math.PI * 2;
// Same constant as planet orbits (generate.ts ORBIT_K), so a rock at radius r keeps
// pace with a planet there (angular speed ∝ r^-1.5) — realistic months/years, not
// the seconds it used to whip around in at 1×.
const BELT_K = 8_000_000;

interface Rock {
  r: number;
  angle: number;
  speed: number;
  y: number;
  quat: Quaternion;
  scale: Vector3;
}

export function AsteroidBelt({ belt }: Props) {
  const ref = useRef<InstancedMesh>(null);

  const rocks = useMemo<Rock[]>(() => {
    const rng = makeRng(belt.seed);
    const list: Rock[] = [];
    const thickness = (belt.outerRadius - belt.innerRadius) * 0.12;
    for (let i = 0; i < belt.count; i++) {
      const r = rng.range(belt.innerRadius, belt.outerRadius);
      const s = rng.range(0.012, 0.04);
      list.push({
        r,
        angle: rng.range(0, TWO_PI),
        speed: TWO_PI / (BELT_K * Math.pow(r, 1.5)),
        y: rng.range(-thickness, thickness),
        quat: new Quaternion().setFromEuler(
          new Euler(rng.range(0, TWO_PI), rng.range(0, TWO_PI), rng.range(0, TWO_PI)),
        ),
        scale: new Vector3(
          s * rng.range(0.7, 1.3),
          s * rng.range(0.7, 1.3),
          s * rng.range(0.7, 1.3),
        ),
      });
    }
    return list;
  }, [belt]);

  const mat = useMemo(() => new Matrix4(), []);
  const pos = useMemo(() => new Vector3(), []);

  useFrame(() => {
    const mesh = ref.current;
    if (!mesh) {
      return;
    }
    const t = gameClock.time;
    for (let i = 0; i < rocks.length; i++) {
      const rk = rocks[i];
      const a = rk.angle + rk.speed * t;
      pos.set(Math.cos(a) * rk.r, rk.y, Math.sin(a) * rk.r);
      mat.compose(pos, rk.quat, rk.scale);
      mesh.setMatrixAt(i, mat);
    }
    mesh.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh ref={ref} args={[undefined, undefined, belt.count]}>
      <icosahedronGeometry args={[1, 0]} />
      <meshStandardMaterial color="#7a7066" flatShading roughness={1} metalness={0.05} />
    </instancedMesh>
  );
}
